/**
 * Public VoxPont engine release registry.
 *
 * WHAT: Defines the numbered engines users may select before authentication.
 * WHY: Auth must offer every routed session engine; unrouted harnesses stay off this list.
 * INVARIANT: VoxPont 12 is the default flagship. Every entry has a matching /voxpont{id} page.
 * Coupling: VoxPontAuthPage, VoxPontOnboardingPage, Firestore users.voxpontEngine validation.
 */

export const DEFAULT_VOXPONT_ENGINE_ID = '12';
export const VOXPONT_ENGINE_PREFERENCE_STORAGE_KEY = 'VoxPont.preferredEngine.v1';

function releasedEngine(id, title, description) {
  return Object.freeze({
    id,
    route: `/voxpont${id}`,
    title,
    description,
  });
}

export const VOXPONT_RELEASED_ENGINES = Object.freeze([
  releasedEngine(
    DEFAULT_VOXPONT_ENGINE_ID,
    'Adaptive medical flagship',
    'The current VoxPont medical translation experience.'
  ),
  releasedEngine('2', 'Live translate (v2)', 'Earlier medical live-translate session.'),
  releasedEngine('3', 'Live translate (v3)', 'Earlier medical live-translate session.'),
  releasedEngine('4', 'Medical (v4)', 'Medical translation on the VoxPont 4 session.'),
  releasedEngine('5', 'Medical (v5)', 'Medical translation on the VoxPont 5 session.'),
  releasedEngine('20', 'Education', 'Teacher-parent conference interpretation.'),
  releasedEngine('30', 'Crisis', 'Emergency dispatch and first-response interpretation.'),
  releasedEngine('40', 'Pharmacy', 'Medication counseling with drug-name verification.'),
  releasedEngine('50', 'Kiosk', 'Waiting-room tablet interpretation.'),
  releasedEngine('60', 'Social Services', 'Benefits and housing case-management interpretation.'),
  releasedEngine('70', 'Low Bandwidth', 'Cascade translation for poor-connectivity settings.'),
  releasedEngine('100', 'VoxPont 100', 'Numbered VoxPont 100 session.'),
  releasedEngine('201', '201 Medical', 'Medical translation on the VoxPont 201 session.'),
  releasedEngine('202', '202 Medical Plus', 'Medical translation on the VoxPont 202 session.'),
  releasedEngine('204', 'Let The Man Speak', 'Numbered VoxPont 204 session.'),
  releasedEngine('205', 'Flexible Speaker Order', 'Numbered VoxPont 205 session.'),
  releasedEngine('301', '301 Offline', 'Offline-capable VoxPont 301 session.'),
  releasedEngine('302', '302 Offline', 'Offline-capable VoxPont 302 session.'),
  releasedEngine('501', '501 LocalHost', 'Numbered VoxPont 501 session.'),
  releasedEngine('700', 'Test console', 'Harness console for configuring and reviewing runs.'),
  // Experimental proximity / interaction catalog (Brief v6) — compose shared paired modules.
  releasedEngine('110', 'Touch (VP110)', 'Bring two phones together to start an ephemeral conversation.'),
  releasedEngine('120', 'Nearby (VP120)', 'Discover and connect nearby VoxPont users without contact.'),
  releasedEngine('130', 'Link (VP130)', 'Universal QR / deep-link pairing across devices.'),
  releasedEngine('140', 'Split (VP140)', 'Stereo earbud pair: language A left, B right.'),
  releasedEngine('150', 'Interpreter (VP150)', 'Private earbud translation vs phone speaker.'),
  releasedEngine('160', 'Dual (VP160)', 'One phone to two independent Bluetooth audio endpoints.'),
  releasedEngine('170', 'Pair (VP170)', 'Two phones; each participant uses their own device audio.'),
  releasedEngine('180', 'Whisper (VP180)', 'Private in-ear for the other person; reply aloud from phone.'),
  releasedEngine('190', 'AutoLanguage (VP190)', 'Detect speaker languages and adapt the language pair.'),
  releasedEngine('200', 'Face-to-Face (VP200)', '180-degree split UI so each person sees oriented text.'),
  releasedEngine('210', 'Earbud (VP210)', 'Continuous conversational translation through earbuds.'),
  releasedEngine('220', 'Voice (VP220)', 'Preserve vocal character in synthetic translated speech.'),
  releasedEngine('230', 'Did You Mean (VP230)', 'Ask for clarification on consequential ambiguity.'),
  releasedEngine('240', 'BackTranslate (VP240)', 'Silent A→B→A check; flag material divergence.'),
  releasedEngine('250', 'Numbers (VP250)', 'Extract doses, dates, currencies alongside translation.'),
  releasedEngine('260', 'Session Memory (VP260)', 'Temporary session context, discarded unless saved.'),
  releasedEngine('270', 'Context Packs (VP270)', 'Domain terminology packs for specialized conversations.'),
  releasedEngine('280', 'Group (VP280)', 'Multi-participant multilingual room with attribution.'),
  releasedEngine('290', 'Broadcast (VP290)', 'One speaker to many listeners in preferred languages.'),
  releasedEngine('300', 'Lens (VP300)', 'Camera translation of signs, menus, and documents.'),
  releasedEngine('310', 'Replay (VP310)', 'Replay, slow down, or alternate a prior translation.'),
  releasedEngine('320', 'Clarify (VP320)', 'Explain idioms and culture-bound concepts explicitly.'),
  releasedEngine('330', 'Rescue (VP330)', 'Fall back to on-device speech/translation when offline.'),
  releasedEngine('340', 'Seamless (VP340)', 'Touch → Nearby → Link into a two-phone conversation.'),
  releasedEngine('350', 'Private Interpreter (VP350)', 'Whisper + AutoLanguage + earbud traveler mode.'),
  releasedEngine('360', 'Clinical Bridge (VP360)', 'Pair + TrustGuard integrity for medical conversations.'),
  releasedEngine('370', 'TableTalk (VP370)', 'Face-to-Face shared bilingual surface on one phone.'),
  releasedEngine('380', 'Travel Companion (VP380)', 'Whisper + Lens + Rescue for travel.'),
  releasedEngine('390', 'Conversation Room (VP390)', 'Group room with context packs and replay.'),
  releasedEngine('400', 'LiveCast (VP400)', 'Broadcast with AutoLanguage and Rescue.'),
  releasedEngine('410', 'Shared Interpreter (VP410)', 'Split stereo shared-earbud interpreter.'),
  releasedEngine('420', 'TrustGuard (VP420)', 'Composable ambiguity, back-translate, and numbers checks.'),
  releasedEngine('430', 'Offline Bridge (VP430)', 'Link/Pair with automatic offline Rescue handoff.'),
]);

/** Map numeric auth engine id → VP model id (e.g. 130 → VP130). */
export function experimentalModelIdFromEngineId(engineId) {
  const id = String(engineId || '').trim();
  if (!/^(1[1-9]0|2[0-9]0|3[0-9]0|4[0-3]0)$/.test(id)) return null;
  return `VP${id}`;
}

function isSelectableEngineId(id) {
  return /^\d+$/.test(id) && Number(id) > 0;
}

/**
 * Throws when a proposed released-engine registry violates public release invariants.
 * Exported so release tooling and tests can validate candidate registries before shipping.
 */
export function assertValidVoxPontEngineRegistry(engines) {
  if (!Array.isArray(engines) || engines.length === 0) {
    throw new Error('At least one released VoxPont engine is required.');
  }

  const ids = new Set();
  const routes = new Set();
  const titles = new Set();

  engines.forEach((engine) => {
    const id = String(engine?.id || '').trim();
    const route = String(engine?.route || '').trim();
    const title = String(engine?.title || '').trim();
    if (!id || !route || !title) {
      throw new Error('Each released VoxPont engine requires id, route, and title.');
    }
    if (!isSelectableEngineId(id)) {
      throw new Error(`Invalid public VoxPont engine id: ${id}`);
    }
    if (route !== `/voxpont${id}`) {
      throw new Error(`VoxPont engine ${id} must use route /voxpont${id}.`);
    }
    const normalizedTitle = title.toLocaleLowerCase();
    if (ids.has(id) || routes.has(route) || titles.has(normalizedTitle)) {
      throw new Error(`Duplicate VoxPont engine metadata for ${id}.`);
    }
    ids.add(id);
    routes.add(route);
    titles.add(normalizedTitle);
  });

  if (!ids.has(DEFAULT_VOXPONT_ENGINE_ID)) {
    throw new Error(`Default VoxPont engine ${DEFAULT_VOXPONT_ENGINE_ID} is not released.`);
  }

  return true;
}

assertValidVoxPontEngineRegistry(VOXPONT_RELEASED_ENGINES);

const ENGINE_BY_ID = new Map(VOXPONT_RELEASED_ENGINES.map((engine) => [engine.id, engine]));

export function getReleasedVoxPontEngine(engineId) {
  if (engineId === null || engineId === undefined) return null;
  return ENGINE_BY_ID.get(String(engineId).trim()) || null;
}

export function resolveReleasedVoxPontEngine(engineId) {
  return (
    getReleasedVoxPontEngine(engineId) ||
    ENGINE_BY_ID.get(DEFAULT_VOXPONT_ENGINE_ID)
  );
}

export function resolveVoxPontPostAuthRoute(explicitRoute, engineId) {
  return explicitRoute || resolveReleasedVoxPontEngine(engineId).route;
}

export function readVoxPontEnginePreference() {
  if (typeof window === 'undefined') return DEFAULT_VOXPONT_ENGINE_ID;
  try {
    return resolveReleasedVoxPontEngine(
      window.localStorage?.getItem(VOXPONT_ENGINE_PREFERENCE_STORAGE_KEY)
    ).id;
  } catch {
    return DEFAULT_VOXPONT_ENGINE_ID;
  }
}

export function persistVoxPontEnginePreference(engineId) {
  const resolved = resolveReleasedVoxPontEngine(engineId);
  if (typeof window !== 'undefined') {
    try {
      window.localStorage?.setItem(VOXPONT_ENGINE_PREFERENCE_STORAGE_KEY, resolved.id);
    } catch {
      // Storage can be unavailable in private browsing; the in-memory form value still works.
    }
  }
  return resolved.id;
}

const voxPontEngineRegistry = {
  DEFAULT_VOXPONT_ENGINE_ID,
  VOXPONT_ENGINE_PREFERENCE_STORAGE_KEY,
  VOXPONT_RELEASED_ENGINES,
  assertValidVoxPontEngineRegistry,
  getReleasedVoxPontEngine,
  resolveReleasedVoxPontEngine,
  resolveVoxPontPostAuthRoute,
  readVoxPontEnginePreference,
  persistVoxPontEnginePreference,
  experimentalModelIdFromEngineId,
};

export default voxPontEngineRegistry;
